import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress
} from '@mui/material';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Currency {
  id: string;
  name: string;
  description: string;
  code: string;
  exchangeRate: number;
  isDefault: boolean;
  active: boolean;
}

interface CurrencyDialogProps {
  open: boolean;
  currency: Currency | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface CurrencyFormData {
  name: string;
  description: string;
  code: string;
  exchangeRate: number;
  isDefault: boolean;
  active: boolean;
}

const CurrencyDialog: React.FC<CurrencyDialogProps> = ({
  open,
  currency,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<CurrencyFormData>({
    name: '',
    description: '',
    code: '',
    exchangeRate: 0,
    isDefault: false,
    active: true
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CurrencyFormData, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useAppDispatch();

  const isEditing = !!currency;

  // Reset form when dialog opens/closes or currency changes
  useEffect(() => {
    if (open) {
      if (currency) {
        setFormData({
          name: currency.name,
          description: currency.description,
          code: currency.code,
          exchangeRate: currency.exchangeRate,
          isDefault: currency.isDefault,
          active: currency.active
        });
      } else {
        setFormData({
          name: '',
          description: '',
          code: '',
          exchangeRate: 0,
          isDefault: false,
          active: true
        });
      }
      setErrors({});
    }
  }, [open, currency]);

  // Handle input changes
  const handleInputChange = (field: keyof CurrencyFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value: any;

    if (field === 'active' || field === 'isDefault') {
      value = event.target.checked;
    } else if (field === 'exchangeRate') {
      value = parseFloat(event.target.value) || 0;
    } else {
      value = event.target.value;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CurrencyFormData, string>> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Currency name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Currency name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Currency name cannot exceed 100 characters';
    }

    // Code validation
    if (!formData.code.trim()) {
      newErrors.code = 'Currency code is required';
    } else if (formData.code.trim().length < 1) {
      newErrors.code = 'Currency code must be at least 1 character';
    } else if (formData.code.trim().length > 10) {
      newErrors.code = 'Currency code cannot exceed 10 characters';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 5) {
      newErrors.description = 'Description must be at least 5 characters';
    } else if (formData.description.trim().length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    // Exchange rate validation
    if (formData.exchangeRate < 0) {
      newErrors.exchangeRate = 'Exchange rate cannot be negative';
    } else if (formData.exchangeRate > 999999999) {
      newErrors.exchangeRate = 'Exchange rate is too large';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const payload: any = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        code: formData.code.trim(),
        exchangeRate: formData.exchangeRate,
        isDefault: formData.isDefault
      };

      // Add id for updates
      if (isEditing) {
        payload.id = currency!.id;
      }

      const endpoint = isEditing ? 'api/Currency/update' : 'api/Currency/create';
      const response = await DataService.post(endpoint, payload);

      if (response.ok) {
        onSuccess();
      } else {
        // Try to get error message from response
        let errorMessage = `Failed to ${isEditing ? 'update' : 'create'} currency`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            // Handle validation errors
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          // Use default error message
        }

        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: `Network error. Please check your connection and try again.`,
          type: 'error'
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h6" component="div">
            {isEditing ? 'Edit Currency' : 'Add New Currency'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditing
              ? 'Update the currency information below'
              : 'Fill in the details to create a new currency'
            }
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Currency Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            disabled={submitting}
            fullWidth
            placeholder="Enter currency name (e.g., US Dollar, Euro, British Pound)"
            variant="outlined"
          />

          <TextField
            label="Currency Code"
            value={formData.code}
            onChange={handleInputChange('code')}
            error={!!errors.code}
            helperText={errors.code}
            disabled={submitting}
            fullWidth
            placeholder="Enter currency code/symbol (e.g., $, €, £)"
            variant="outlined"
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={handleInputChange('description')}
            error={!!errors.description}
            helperText={errors.description}
            disabled={submitting}
            fullWidth
            multiline
            rows={4}
            placeholder="Describe this currency and where it's used"
            variant="outlined"
          />

          <TextField
            label="Exchange Rate"
            type="number"
            value={formData.exchangeRate}
            onChange={handleInputChange('exchangeRate')}
            error={!!errors.exchangeRate}
            helperText={errors.exchangeRate || 'Exchange rate relative to base currency (0 for no conversion)'}
            disabled={submitting}
            fullWidth
            placeholder="Enter exchange rate (e.g., 89500 for LBP to USD)"
            variant="outlined"
            inputProps={{ min: 0, step: 0.01 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isDefault}
                onChange={handleInputChange('isDefault')}
                disabled={submitting}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  {formData.isDefault ? 'Default Currency' : 'Regular Currency'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formData.isDefault
                    ? 'This will be the default currency for the system'
                    : 'This currency can be selected when creating transactions'
                  }
                </Typography>
              </Box>
            }
          />

          {isEditing && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleInputChange('active')}
                  disabled={submitting}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {formData.active ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.active
                      ? 'This currency is currently active and available for use'
                      : 'This currency is inactive and will not be available for selection'
                    }
                  </Typography>
                </Box>
              }
            />
          )}

          {/* Information for new currencies */}
          {!isEditing && (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 1,
                border: '1px solid rgba(0, 0, 0, 0.12)'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                Currency Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Once created, this currency will be available for selection in rental agreements.
                Set an exchange rate for automatic conversions, and mark as default if this should be the primary currency.
                Common currencies include USD ($), EUR (€), GBP (£), etc.
              </Typography>
            </Box>
          )}
        </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting
              ? `${isEditing ? 'Updating' : 'Creating'}...`
              : `${isEditing ? 'Update' : 'Create'} Currency`
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CurrencyDialog;